/** @forge-header
 * @id          golfParser
 * @role        Golf ticker parser | KXPRESCUP (team-event winner futures)
 * @pattern     pure-fn
 * @purity      none
 * @upstream    kalshiTicker/types:ParsedTicker | kalshiTicker/helpers:parseFallback
 * @downstream  kalshiTicker/parse
 * @exports     parseGolfFuture:fn
 * @test        parse.test.ts
 */

import type { ParsedTicker } from '../types';
import { parseFallback } from '../helpers';

/** Series → event name. Golf on Kalshi is futures-shaped (no game, no date):
 *  `KXPRESCUP-26-USA` is "Presidents Cup Winner · Team USA". Only series the
 *  plane carries are named; anything else humanises its series token. */
const GOLF_SERIES_LABELS: Record<string, string> = {
  KXPRESCUP: 'Presidents Cup',
  KXPGARYDER: 'Ryder Cup',
  KXRYDERCUP: 'Ryder Cup',
};

const GOLF_TEAM_LABELS: Record<string, string> = {
  USA: 'Team USA',
  WORLD: 'Team World',
  EUR: 'Team Europe',
  EUROPE: 'Team Europe',
  TIE: 'Tie',
};

export function parseGolfFuture(raw: string): ParsedTicker {
  const parts = raw.split('-');
  if (parts.length < 2) return parseFallback(raw);
  const series = parts[0] ?? '';
  const outcome = parts.length >= 3 ? parts[parts.length - 1] ?? null : null;
  const eventLabel = GOLF_SERIES_LABELS[series] ?? series.replace(/^KX/, '').replace(/([A-Z])([A-Z]+)/g, (_m, a: string, b: string) => `${a}${b.toLowerCase()}`);
  const yy = parts[1] ?? '';
  const year = /^\d{2}$/.test(yy) ? `20${yy}` : null;
  const outcomeLabel = outcome ? GOLF_TEAM_LABELS[outcome] ?? outcome : null;
  const shortLabel = `Golf: ${eventLabel}${year ? ` ${year}` : ''}`;
  const fullLabel = outcomeLabel ? `${shortLabel} · ${outcomeLabel}` : shortLabel;
  return {
    raw,
    category: 'golf-future',
    series,
    date: null,
    game: null,
    player: null,
    spread: null,
    total: null,
    tennis: null,
    outcome,
    shortLabel,
    fullLabel,
  };
}
